import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Tooltip,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    esVisitaAbierta,
    getVisitaEstadoChipSx,
    visitaEstadoChipMap,
    VisitaEstadoChipConfig
} from '../consts/visitasStatus';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../consts/visitTypeDisplayNames';

interface Visita {
    id?: number;
    idTipoVisita?: number;
    nombreTipoVisita?: string;
    estado?: number;
    nombreEstado?: string;
    fechaVisita?: string;
    fechaLimite?: string;
    actividades?: Array<{ tipoVisita?: { id?: number; descripcion?: string } }>;
}

interface VisitaBadge {
    key: string;
    label: string;
    tiposDetalle?: string;
    estado?: number;
    nombreEstado?: string;
    fechaVisita?: string;
    fechaLimite?: string;
    tiposVisitaIds: number[];
}

const MULTIPLE_LABEL = 'MÚLTIPLE';

interface Props {
    visitas: Visita[];
    loading?: boolean;
    defaultExpanded?: boolean;
    tiposVisitaIncompatibles?: Set<number>;
    tieneConflicto?: boolean;
    haySeleccionTipoVisita?: boolean;
}

const DEFAULT_CHIP_CONFIG: VisitaEstadoChipConfig = {
    textColor: '#384A52',
    backgroundColor: '#ECECEC',
    borderColor: '#A7B1B5',
    text: 'Desconocido'
};

const formatFecha = (fecha?: string): string | null => {
    if (!fecha) return null;
    const parsed = dayjs(fecha);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : null;
};

// El tipo de visita real vive en cada actividad, no en el nivel superior de la visita
// (mismo criterio que usa la grilla principal, ver VisitasIndex.tsx: getTipoVisitaLabel).
const getTipoVisitaLabel = (tipoVisita?: { id?: number; descripcion?: string }): string =>
    (tipoVisita?.id && VISIT_TYPE_DISPLAY_NAME_OVERRIDES[tipoVisita.id]) ||
    tipoVisita?.descripcion ||
    'N/A';

// Un badge por visita: si tiene más de una actividad se colapsa a "MÚLTIPLE"
// con el detalle de los tipos en el tooltip, igual que la grilla principal
// (ver VisitasIndex.tsx: columna "Tipo de Visita").
const getBadgesFromVisitas = (visitas: Visita[]): VisitaBadge[] =>
    visitas.filter((visita) => esVisitaAbierta(visita.estado)).map((visita, visitaIndex) => {
        const actividades =
            visita.actividades && visita.actividades.length > 0
                ? visita.actividades
                : [{ tipoVisita: { id: visita.idTipoVisita, descripcion: visita.nombreTipoVisita } }];

        const tiposLabels = actividades.map((a) => getTipoVisitaLabel(a?.tipoVisita)).filter(Boolean);
        const esMultiple = actividades.length > 1;

        return {
            key: String(visita.id ?? visitaIndex),
            label: esMultiple ? MULTIPLE_LABEL : tiposLabels[0] || 'N/A',
            tiposDetalle: esMultiple ? tiposLabels.join(', ') : undefined,
            estado: visita.estado,
            nombreEstado: visita.nombreEstado,
            fechaVisita: visita.fechaVisita,
            fechaLimite: visita.fechaLimite,
            // Una visita "MÚLTIPLE" también puede estar en conflicto si alguna de sus
            // actividades es del tipo seleccionado (ver findTipoVisitaConflictos).
            tiposVisitaIds: actividades
                .map((a: any) => a?.tipoVisita?.id)
                .filter((id: unknown): id is number => id != null)
        };
    });

const EstablecimientoVisitasInfo: React.FC<Props> = ({
    visitas,
    loading,
    defaultExpanded,
    tiposVisitaIncompatibles,
    tieneConflicto,
    haySeleccionTipoVisita
}) => {
    const { t } = useTranslation('visitas');
    const [expanded, setExpanded] = useState(Boolean(defaultExpanded));

    // Si el establecimiento pasa a ser incompatible (borde rojo) se auto-expande para que el
    // usuario vea de inmediato la visita abierta que genera el conflicto; nunca se auto-colapsa.
    useEffect(() => {
        if (defaultExpanded) setExpanded(true);
    }, [defaultExpanded]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                <CircularProgress size={14} />
            </Box>
        );
    }

    const badges = getBadgesFromVisitas(visitas || []);

    // Una vez elegido al menos un tipo de visita, la leyenda deja de depender de si existen
    // visitas abiertas en general y pasa a depender de si alguna coincide con los tipos
    // seleccionados (SMP-373 HU-02): "Sin visitas abiertas" también cuando hay abiertas pero
    // de otro tipo. Sin ningún tipo seleccionado, se mantiene el comportamiento de siempre.
    const mostrarComoVisitasAbiertas = haySeleccionTipoVisita
        ? Boolean(tieneConflicto)
        : badges.length > 0;

    if (!mostrarComoVisitasAbiertas) {
        const sinVisitasAbiertas = haySeleccionTipoVisita || (visitas?.length ?? 0) > 0;
        return (
            <Typography variant="body2" sx={{ mt: 0.75, color: 'text.disabled' }}>
                {t(sinVisitasAbiertas ? 'newVisit.withoutOpenVisits' : 'newVisit.withoutVisits')}
            </Typography>
        );
    }

    // Tooltip (HU-03): al pasar el mouse o enfocar la leyenda, previsualiza todas las visitas
    // abiertas del establecimiento sin necesidad de desplegar el acordeón, resaltando las que
    // coinciden con los tipos seleccionados.
    const mostrarTooltipLeyenda = Boolean(haySeleccionTipoVisita && tieneConflicto);
    const etiquetaVisitasAbiertas = (
        <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#384A52' }}
            tabIndex={mostrarTooltipLeyenda ? 0 : undefined}
        >
            {t('newVisit.openVisits')}
        </Typography>
    );

    return (
        <Accordion
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={() => setExpanded((prev) => !prev)}
            sx={{
                mt: 0.75,
                borderRadius: 1.5,
                overflow: 'hidden',
                backgroundColor: 'transparent',
                boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.10)',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { boxShadow: '-1px 1px 4px rgba(0, 0, 0, 0.10)' }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" sx={{ color: '#384A52' }} />}
                sx={{
                    minHeight: 0,
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: '#fff',
                    color: '#384A52',
                    fontWeight: 'inherit',
                    '& .MuiAccordionSummary-content': { margin: 0 },
                    '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0 }
                }}
            >
                {mostrarTooltipLeyenda ? (
                    <Tooltip
                        arrow
                        title={
                            <Box>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                                    {t('newVisit.openVisitsTooltipTitle')}
                                </Typography>
                                {badges.map((badge) => {
                                    const coincide = badge.tiposVisitaIds.some((id) =>
                                        tiposVisitaIncompatibles?.has(id)
                                    );

                                    return (
                                        <Typography
                                            key={badge.key}
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                fontWeight: coincide ? 700 : 400,
                                                color: coincide ? 'warning.light' : 'inherit'
                                            }}
                                        >
                                            {`${badge.tiposDetalle || badge.label} — ${
                                                badge.nombreEstado ||
                                                visitaEstadoChipMap[badge.estado as keyof typeof visitaEstadoChipMap]
                                                    ?.text ||
                                                DEFAULT_CHIP_CONFIG.text
                                            }`}
                                        </Typography>
                                    );
                                })}
                            </Box>
                        }
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: '#384A52 !important',
                                    fontSize: '0.875rem',
                                    maxWidth: 280
                                }
                            }
                        }}
                    >
                        {etiquetaVisitasAbiertas}
                    </Tooltip>
                ) : (
                    etiquetaVisitasAbiertas
                )}
            </AccordionSummary>

            <AccordionDetails sx={{ px: 1.5, pt: 0.5, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {badges.map((badge) => {
                        const chipConfig =
                            visitaEstadoChipMap[badge.estado as keyof typeof visitaEstadoChipMap] ||
                            DEFAULT_CHIP_CONFIG;
                        const fechaVisita = formatFecha(badge.fechaVisita);
                        const fechaLimite = formatFecha(badge.fechaLimite);
                        const esIncompatible = badge.tiposVisitaIds.some((id) =>
                            tiposVisitaIncompatibles?.has(id)
                        );

                        return (
                            <Tooltip
                                key={badge.key}
                                arrow
                                title={
                                    <Box>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            {`${t('filters.visitType')}: ${badge.tiposDetalle || badge.label}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            {`${t('table.headers.state', { defaultValue: 'Estado' })}: ${
                                                badge.nombreEstado || chipConfig.text
                                            }`}
                                        </Typography>
                                        {fechaVisita && (
                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                {`${t('filters.visitDate', { defaultValue: 'Fecha de visita' })}: ${fechaVisita}`}
                                            </Typography>
                                        )}
                                        {!fechaVisita && fechaLimite && (
                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                {`${t('filters.deadlineDate')}: ${fechaLimite}`}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            backgroundColor: '#384A52 !important',
                                            fontSize: '0.875rem',
                                            maxWidth: 260
                                        }
                                    }
                                }}
                            >
                                <Chip
                                    label={badge.label}
                                    size="small"
                                    sx={{
                                        ...getVisitaEstadoChipSx(chipConfig),
                                        fontSize: 11,
                                        maxWidth: 220,
                                        cursor: 'default',
                                        '& .MuiChip-label': { cursor: 'default' },
                                        ...(esIncompatible && {
                                            border: '2px solid',
                                            borderColor: 'warning.main'
                                        })
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default EstablecimientoVisitasInfo;
